import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { PresenceContactFilterOptions } from '../models/presence-contact-filter-option';
import { PresenceContactRegisterOptions } from '../models/presence-contact-register-options';
import {
   GetPresenceContactRegister,
   GetPresenceContactRegisterVariables,
} from '../queries/generated/GetPresenceContactRegister';
import { getPresenceContactQuery } from '../queries/getPresenceContactRegister.graphql';

@Injectable({ providedIn: 'root' })
export class PresenceContactRegisterApiService {
   constructor(private apiService: LcsApiService, private httpClient: HttpClient) {}

   getPresenceContacts(
      endCursor: string | null,
      filters: PresenceContactFilterOptions[] | null,
      quickFilters: PresenceContactRegisterOptions | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetPresenceContactRegister>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertPresenceContactFilterOptions.bind(this)),
              }
            : { filterOptions: [] };
      if (quickFilters) {
         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim();
            const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };
            subExpression.filterOptions.push({
               field: 'DisplayName',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            const extensionNumberRegex = /\d+/;
            const numericSearchText = searchText.match(extensionNumberRegex)?.[0];
            if (numericSearchText) {
               subExpression.filterOptions.push({
                  field: 'Extension.Number',
                  operation: FilterOperations.Contains,
                  value: numericSearchText,
               });
            }
            subExpression.filterOptions.push({
               field: 'Extension.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            filterBuilderFilterExpression.subExpressions = [subExpression];
         }
      }
      const variables = this.getPresenceContactsVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetPresenceContactRegister, GetPresenceContactRegisterVariables>({
         query: getPresenceContactQuery,
         variables,
      });
   }

   private convertPresenceContactFilterOptions(option: PresenceContactFilterOptions): FilterOption {
      return {
         basePath: option.field,
         field: option.field,
         operation: option.operation,
         useAllForNotIn: true,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: PresenceContactFilterOptions): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'presenceContact':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.extensionPhoneId;
                 })
               : option.value?.extensionPhoneId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
      return null;
   }

   private getPresenceContactsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetPresenceContactRegisterVariables {
      const variables: GetPresenceContactRegisterVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };

      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   initiateDirectedCall(initiateDirectedCallData: any) {
      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json',
         }),
      };
      this.httpClient
         .post(
            this.apiService.buildApiUrl('call/InitiateDirectedCall'),
            JSON.stringify(initiateDirectedCallData),
            httpOptions
         )
         .subscribe();
   }
}
